import React, { useState } from 'react';
import './RadiologyStructuredFindingsDrawer.css';

/**
 * RadiologyStructuredFindingsDrawer
 * Structured organ check-list and measurement inputs.
 * Supports One-Click Normal, quick abnormality tagging, and
 * seamless insertion into the rich narrative editor.
 */
export default function RadiologyStructuredFindingsDrawer({
  isOpen,
  onClose,
  department = 'Radiology',
  itemName = '',
  onInsertFindings,
  onMarkAllNormal
}) {
  // Determine anatomical category based on test item name or modality
  const isChest = /chest|thorax|lung|rib|pleura/i.test(itemName);
  const isBrain = /brain|head|cranium|mri brain|ct head/i.test(itemName);
  // Default to Abdomen / General
  const isAbdomen = !isChest && !isBrain;

  // 1. Abdomen Structured State
  const [abdoFindings, setAbdoFindings] = useState({
    liver: 'Normal', // Normal, Grade I Fatty, Grade II Fatty, Hepatomegaly, Solitary Mass
    gallbladder: 'Normal', // Normal, Cholelithiasis, Thick Wall (>3mm), Contracted
    spleen: 'Normal', // Normal, Splenomegaly
    rightKidney: 'Normal', // Normal, Calculus, Cyst, Hydronephrosis
    leftKidney: 'Normal', // Normal, Calculus, Cyst, Hydronephrosis
    urinaryBladder: 'Normal', // Normal, Calculus, Wall Thickening
    peritoneum: 'No Free Fluid', // No Free Fluid, Minimal Ascites, Moderate Ascites
    rightKidneySize: '10.5',
    leftKidneySize: '10.4',
    spleenSize: '9.8'
  });

  // 2. Chest Structured State
  const [chestFindings, setChestFindings] = useState({
    trachea: 'Midline',
    lungs: 'Clear', // Clear, Right Lower Consolidation, Bilateral Infiltrates, Apical Cavitation
    pleura: 'No Effusion', // No Effusion, Right Pleural Effusion, Bilateral Pleural Effusion
    heart: 'Normal Size (<0.50)', // Normal Size, Cardiomegaly
    mediastinum: 'Normal',
    bones: 'Intact' // Intact, Rib Fracture, Degenerative Changes
  });

  // 3. Brain Structured State
  const [brainFindings, setBrainFindings] = useState({
    parenchyma: 'Normal Signal', // Normal Signal, Acute MCA Infarct, Periventricular Ischemia
    ventricles: 'Normal Size', // Normal Size, Hydrocephalus, Prominent ex-vacuo
    midlineShift: 'No Midline Shift', // No Midline Shift, Right Shift, Left Shift
    hemorrhage: 'No Acute Hemorrhage', // No Acute Hemorrhage, Acute SDH, EDH, SAH
    calvarium: 'Intact'
  });

  // 4. Smart Configured Finding Generator State
  const [smartFinding, setSmartFinding] = useState({
    type: 'Renal Calculus',
    measurement: '6 mm',
    location: 'Lower pole of right kidney',
    secondarySign: 'Mild hydronephrosis'
  });

  if (!isOpen) return null;

  // One-Click Mark All Normal
  const handleMarkNormal = () => {
    if (isAbdomen) {
      setAbdoFindings({
        liver: 'Normal',
        gallbladder: 'Normal',
        spleen: 'Normal',
        rightKidney: 'Normal',
        leftKidney: 'Normal',
        urinaryBladder: 'Normal',
        peritoneum: 'No Free Fluid',
        rightKidneySize: '10.5',
        leftKidneySize: '10.4',
        spleenSize: '9.8'
      });
    } else if (isChest) {
      setChestFindings({
        trachea: 'Midline',
        lungs: 'Clear',
        pleura: 'No Effusion',
        heart: 'Normal Size (<0.50)',
        mediastinum: 'Normal',
        bones: 'Intact'
      });
    } else if (isBrain) {
      setBrainFindings({
        parenchyma: 'Normal Signal',
        ventricles: 'Normal Size',
        midlineShift: 'No Midline Shift',
        hemorrhage: 'No Acute Hemorrhage',
        calvarium: 'Intact'
      });
    }

    if (onMarkAllNormal) {
      onMarkAllNormal(isAbdomen ? 'abdomen' : (isChest ? 'chest' : 'brain'));
    }
  };

  // Compile structured state into formatted HTML narrative
  const handleGenerateAndInsert = () => {
    let html = '';
    let impression = '';

    if (isAbdomen) {
      const isFatty = abdoFindings.liver.includes('Fatty');
      const hasStone = abdoFindings.gallbladder.includes('Cholelithiasis');
      const hasRtKidneyFinding = abdoFindings.rightKidney !== 'Normal';
      const hasLtKidneyFinding = abdoFindings.leftKidney !== 'Normal';

      html = `
<p><strong>STRUCTURED ABDOMINAL FINDINGS:</strong></p>
<p>• <strong>LIVER:</strong> ${abdoFindings.liver === 'Normal' ? 'Normal size, smooth contour, and homogeneous parenchymal echotexture. No focal mass lesion.' : abdoFindings.liver + ' noted with increased parenchymal echogenicity.'}<br>
• <strong>GALLBLADDER:</strong> ${abdoFindings.gallbladder === 'Normal' ? 'Well distended, wall thickness is normal. Lumen is clear without calculus or sludge.' : abdoFindings.gallbladder + ' noted.'}<br>
• <strong>SPLEEN:</strong> ${abdoFindings.spleen === 'Normal' ? `Normal in size (${abdoFindings.spleenSize} cm) and parenchymal echotexture.` : abdoFindings.spleen + ` (measuring ${abdoFindings.spleenSize} cm).`}<br>
• <strong>KIDNEYS:</strong> Right kidney measures ${abdoFindings.rightKidneySize} cm (${abdoFindings.rightKidney}). Left kidney measures ${abdoFindings.leftKidneySize} cm (${abdoFindings.leftKidney}). Corticomedullary differentiation is preserved.<br>
• <strong>URINARY BLADDER:</strong> ${abdoFindings.urinaryBladder === 'Normal' ? 'Well distended with thin, smooth wall.' : abdoFindings.urinaryBladder}<br>
• <strong>PERITONEAL CAVITY:</strong> ${abdoFindings.peritoneum}.</p>`;

      if (!isFatty && !hasStone && !hasRtKidneyFinding && !hasLtKidneyFinding) {
        impression = 'Normal whole abdomen ultrasound study. No focal abnormality detected.';
      } else {
        const parts = [];
        if (isFatty) parts.push(abdoFindings.liver);
        if (hasStone) parts.push('Cholelithiasis');
        if (hasRtKidneyFinding) parts.push(`Right Kidney: ${abdoFindings.rightKidney}`);
        if (hasLtKidneyFinding) parts.push(`Left Kidney: ${abdoFindings.leftKidney}`);
        impression = parts.join('; ') + '.';
      }
    } else if (isChest) {
      const isConsolidation = abdoFindings.lungs?.includes('Consolidation') || chestFindings.lungs.includes('Consolidation');
      const isCardiomegaly = chestFindings.heart.includes('Cardiomegaly');
      const isEffusion = chestFindings.pleura.includes('Effusion');

      html = `
<p><strong>STRUCTURED CHEST FINDINGS:</strong></p>
<p>• <strong>TRACHEA:</strong> Central and midline.<br>
• <strong>LUNGS:</strong> ${chestFindings.lungs === 'Clear' ? 'Both lung fields are clear and adequately aerated without focal airspace consolidation, mass, or interstitial infiltration.' : chestFindings.lungs + ' noted.'}<br>
• <strong>PLEURA:</strong> ${chestFindings.pleura === 'No Effusion' ? 'Costophrenic and cardiophrenic angles are well preserved bilaterally. No pleural effusion.' : chestFindings.pleura + ' visualized.'}<br>
• <strong>HEART & MEDIASTINUM:</strong> Cardiothoracic ratio is ${chestFindings.heart}. Mediastinal contours are unremarkable.<br>
• <strong>BONY THORAX:</strong> ${chestFindings.bones === 'Intact' ? 'Thoracic skeleton and visual soft tissues are within normal limits.' : chestFindings.bones}.</p>`;

      if (chestFindings.lungs === 'Clear' && !isCardiomegaly && !isEffusion) {
        impression = 'Normal chest radiograph. No acute cardiopulmonary lesion.';
      } else {
        const parts = [];
        if (chestFindings.lungs !== 'Clear') parts.push(chestFindings.lungs);
        if (isCardiomegaly) parts.push('Cardiomegaly');
        if (isEffusion) parts.push(chestFindings.pleura);
        impression = parts.join('; ') + '.';
      }
    } else if (isBrain) {
      html = `
<p><strong>STRUCTURED BRAIN FINDINGS:</strong></p>
<p>• <strong>BRAIN PARENCHYMA:</strong> ${brainFindings.parenchyma === 'Normal Signal' ? 'Normal gray-white matter differentiation. No focal parenchymal signal abnormality or diffusion restriction.' : brainFindings.parenchyma + ' noted.'}<br>
• <strong>VENTRICULAR SYSTEM:</strong> ${brainFindings.ventricles}. Basal cisterns are patent.<br>
• <strong>MIDLINE STRUCTURES:</strong> ${brainFindings.midlineShift}.<br>
• <strong>HEMORRHAGE:</strong> ${brainFindings.hemorrhage}.<br>
• <strong>CALVARIUM:</strong> Intact.</p>`;

      impression = brainFindings.parenchyma === 'Normal Signal' && brainFindings.hemorrhage === 'No Acute Hemorrhage'
        ? 'Normal brain imaging study. No acute infarct, hemorrhage, or mass effect.'
        : `${brainFindings.parenchyma}. ${brainFindings.midlineShift}.`;
    }

    onInsertFindings({ findingsHtml: html, impressionText: impression });
    onClose();
  };

  return (
    <div className="rad-modal-overlay">
      <div className="rad-struct-drawer">
        {/* Header */}
        <div className="rad-struct-header">
          <div>
            <h2>
              <span>🧬</span> Structured Clinical Findings
            </h2>
            <p>
              {itemName} &bull; {isAbdomen ? 'Abdominal Protocol' : (isChest ? 'Thoracic Protocol' : 'Neuro Protocol')}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              type="button"
              className="rad-btn-one-click-normal"
              onClick={handleMarkNormal}
              title="Reset all anatomical regions to normal findings in 1 click"
            >
              ⚡ Mark All Normal
            </button>
            <button className="rad-modal-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Content Body */}
        <div className="rad-struct-body">
          {/* Smart Configured Finding Generator */}
          <div className="rad-smart-generator-card">
            <div className="rad-smart-generator-header">
              <span className="rad-smart-gen-icon">🎯</span>
              <div>
                <h4>Smart Configured Finding Generator</h4>
                <p>Structured parameter-to-narrative generation (Doctor can edit freely; never auto-finalizes)</p>
              </div>
            </div>

            <div className="rad-smart-gen-grid">
              <div className="rad-smart-field">
                <label>Finding</label>
                <select
                  value={smartFinding.type}
                  onChange={e => setSmartFinding({ ...smartFinding, type: e.target.value })}
                >
                  <option value="Renal Calculus">Renal Calculus</option>
                  <option value="Pulmonary Nodule">Pulmonary Nodule</option>
                  <option value="Gallbladder Calculus">Gallbladder Calculus</option>
                  <option value="Pleural Effusion">Pleural Effusion</option>
                  <option value="Hepatic Hemangioma">Hepatic Hemangioma</option>
                  <option value="Intracranial Hemorrhage">Intracranial Hemorrhage</option>
                </select>
              </div>

              <div className="rad-smart-field">
                <label>Measurement</label>
                <input
                  type="text"
                  value={smartFinding.measurement}
                  placeholder="e.g. 6 mm"
                  onChange={e => setSmartFinding({ ...smartFinding, measurement: e.target.value })}
                />
              </div>

              <div className="rad-smart-field">
                <label>Location</label>
                <input
                  type="text"
                  value={smartFinding.location}
                  placeholder="e.g. Lower Pole"
                  onChange={e => setSmartFinding({ ...smartFinding, location: e.target.value })}
                />
              </div>

              <div className="rad-smart-field">
                <label>Secondary Sign</label>
                <input
                  type="text"
                  value={smartFinding.secondarySign}
                  placeholder="e.g. Mild Hydronephrosis"
                  onChange={e => setSmartFinding({ ...smartFinding, secondarySign: e.target.value })}
                />
              </div>
            </div>

            <div className="rad-smart-preview">
              <span className="preview-lbl">Generated Text:</span>
              <p className="preview-text">
                {`A ${smartFinding.type.toLowerCase()} measuring ${smartFinding.measurement} is identified in the ${smartFinding.location.toLowerCase()}${smartFinding.secondarySign ? ', with associated ' + smartFinding.secondarySign.toLowerCase() : ''}.`}
              </p>
              <button
                type="button"
                className="btn-insert-smart"
                onClick={() => {
                  const generated = `<p>• <strong>${smartFinding.type.toUpperCase()}:</strong> A ${smartFinding.type.toLowerCase()} measuring ${smartFinding.measurement} is noted in the ${smartFinding.location.toLowerCase()}${smartFinding.secondarySign ? ', with associated ' + smartFinding.secondarySign.toLowerCase() : ''}.</p>`;
                  onInsertFindings(generated);
                }}
              >
                ➕ Insert Finding into Editor
              </button>
            </div>
          </div>

          {/* Section Divider */}
          <h4 style={{ margin: '16px 0 8px 0', fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Comprehensive Organ Checklists & Biometrics
          </h4>

          {/* ABDOMEN VIEW */}
          {isAbdomen && (
            <div className="rad-struct-grid">
              <div className="rad-struct-card">
                <label>Liver Findings</label>
                <select
                  value={abdoFindings.liver}
                  onChange={e => setAbdoFindings({ ...abdoFindings, liver: e.target.value })}
                >
                  <option value="Normal">Normal Echotexture</option>
                  <option value="Grade I Fatty Infiltration">Grade I Fatty Liver</option>
                  <option value="Grade II Fatty Infiltration">Grade II Fatty Liver</option>
                  <option value="Hepatomegaly (16.2 cm)">Hepatomegaly</option>
                  <option value="Solitary Hepatic Hemangioma (14mm)">Solitary Hemangioma</option>
                </select>
              </div>

              <div className="rad-struct-card">
                <label>Gallbladder</label>
                <select
                  value={abdoFindings.gallbladder}
                  onChange={e => setAbdoFindings({ ...abdoFindings, gallbladder: e.target.value })}
                >
                  <option value="Normal">Normal Lumen & Wall</option>
                  <option value="Cholelithiasis (12mm calculus)">Cholelithiasis (Calculus)</option>
                  <option value="Acute Cholecystitis (Wall edema 4mm)">Acute Cholecystitis</option>
                  <option value="Biliary Sludge">Biliary Sludge</option>
                </select>
              </div>

              <div className="rad-struct-card">
                <label>Right Kidney (cm & status)</label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <input
                    type="text"
                    style={{ width: '60px' }}
                    value={abdoFindings.rightKidneySize}
                    onChange={e => setAbdoFindings({ ...abdoFindings, rightKidneySize: e.target.value })}
                  />
                  <select
                    style={{ flex: 1 }}
                    value={abdoFindings.rightKidney}
                    onChange={e => setAbdoFindings({ ...abdoFindings, rightKidney: e.target.value })}
                  >
                    <option value="Normal">Normal</option>
                    <option value="Renal Calculus (6mm, Mid Pole)">Renal Calculus</option>
                    <option value="Cortical Simple Cyst (18mm)">Simple Cyst</option>
                    <option value="Mild Hydronephrosis">Hydronephrosis</option>
                  </select>
                </div>
              </div>

              <div className="rad-struct-card">
                <label>Left Kidney (cm & status)</label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <input
                    type="text"
                    style={{ width: '60px' }}
                    value={abdoFindings.leftKidneySize}
                    onChange={e => setAbdoFindings({ ...abdoFindings, leftKidneySize: e.target.value })}
                  />
                  <select
                    style={{ flex: 1 }}
                    value={abdoFindings.leftKidney}
                    onChange={e => setAbdoFindings({ ...abdoFindings, leftKidney: e.target.value })}
                  >
                    <option value="Normal">Normal</option>
                    <option value="Renal Calculus (5mm, Lower Pole)">Renal Calculus</option>
                    <option value="Cortical Simple Cyst (12mm)">Simple Cyst</option>
                    <option value="Mild Hydronephrosis">Hydronephrosis</option>
                  </select>
                </div>
              </div>

              <div className="rad-struct-card">
                <label>Spleen</label>
                <select
                  value={abdoFindings.spleen}
                  onChange={e => setAbdoFindings({ ...abdoFindings, spleen: e.target.value })}
                >
                  <option value="Normal">Normal</option>
                  <option value="Splenomegaly (13.5 cm)">Splenomegaly</option>
                </select>
              </div>

              <div className="rad-struct-card">
                <label>Peritoneal Cavity</label>
                <select
                  value={abdoFindings.peritoneum}
                  onChange={e => setAbdoFindings({ ...abdoFindings, peritoneum: e.target.value })}
                >
                  <option value="No Free Fluid">No Free Fluid</option>
                  <option value="Minimal Fluid in Pouch of Douglas">Minimal Fluid in Pelvis</option>
                  <option value="Moderate Ascites in Morison's Pouch">Moderate Ascites</option>
                </select>
              </div>
            </div>
          )}

          {/* CHEST VIEW */}
          {isChest && (
            <div className="rad-struct-grid">
              <div className="rad-struct-card">
                <label>Lung Parenchyma</label>
                <select
                  value={chestFindings.lungs}
                  onChange={e => setChestFindings({ ...chestFindings, lungs: e.target.value })}
                >
                  <option value="Clear">Clear (No Infiltration)</option>
                  <option value="Patchy airspace consolidation in Right Lower Zone (Pneumonia)">RLL Consolidation</option>
                  <option value="Bilateral perihilar reticulonodular opacities">Perihilar Opacities</option>
                  <option value="Cavitary lesion in Left Upper Lobe">Left Upper Cavitary</option>
                </select>
              </div>

              <div className="rad-struct-card">
                <label>Pleural Cavity</label>
                <select
                  value={chestFindings.pleura}
                  onChange={e => setChestFindings({ ...chestFindings, pleura: e.target.value })}
                >
                  <option value="No Effusion">Clear Costophrenic Angles</option>
                  <option value="Blunting of Right CP Angle (Right Pleural Effusion)">Right Pleural Effusion</option>
                  <option value="Bilateral Pleural Effusion">Bilateral Pleural Effusion</option>
                </select>
              </div>

              <div className="rad-struct-card">
                <label>Cardiothoracic Ratio</label>
                <select
                  value={chestFindings.heart}
                  onChange={e => setChestFindings({ ...chestFindings, heart: e.target.value })}
                >
                  <option value="Normal Size (<0.50)">Normal Size (&lt; 0.50)</option>
                  <option value="Increased CTR > 0.55 (Cardiomegaly)">Cardiomegaly (&gt; 0.55)</option>
                </select>
              </div>

              <div className="rad-struct-card">
                <label>Bony Cage</label>
                <select
                  value={chestFindings.bones}
                  onChange={e => setChestFindings({ ...chestFindings, bones: e.target.value })}
                >
                  <option value="Intact">Intact Ribs & Clavicles</option>
                  <option value="Right 5th-6th Rib Fracture without displacement">Rib Fracture</option>
                </select>
              </div>
            </div>
          )}

          {/* BRAIN VIEW */}
          {isBrain && (
            <div className="rad-struct-grid">
              <div className="rad-struct-card">
                <label>Parenchyma / DWI</label>
                <select
                  value={brainFindings.parenchyma}
                  onChange={e => setBrainFindings({ ...brainFindings, parenchyma: e.target.value })}
                >
                  <option value="Normal Signal">Normal Signal (No Acute Infarct)</option>
                  <option value="Restricted diffusion in Left MCA territory (Acute Infarct)">Acute Left MCA Infarct</option>
                  <option value="Chronic Periventricular Microvascular Ischemia">Small Vessel Disease</option>
                </select>
              </div>

              <div className="rad-struct-card">
                <label>Hemorrhage</label>
                <select
                  value={brainFindings.hemorrhage}
                  onChange={e => setBrainFindings({ ...brainFindings, hemorrhage: e.target.value })}
                >
                  <option value="No Acute Hemorrhage">No Intracranial Hemorrhage</option>
                  <option value="Acute Subdural Hematoma (6mm thickness)">Subdural Hematoma</option>
                  <option value="Parenchymal Intracerebral Hemorrhage in Right Basal Ganglia">Intracerebral Hemorrhage</option>
                </select>
              </div>

              <div className="rad-struct-card">
                <label>Midline Shift</label>
                <select
                  value={brainFindings.midlineShift}
                  onChange={e => setBrainFindings({ ...brainFindings, midlineShift: e.target.value })}
                >
                  <option value="No Midline Shift">No Midline Shift</option>
                  <option value="4 mm Shift to the Right">4 mm Right Shift</option>
                  <option value="3 mm Shift to the Left">3 mm Left Shift</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="rad-struct-footer">
          <button type="button" className="rad-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="rad-btn-insert-findings"
            onClick={handleGenerateAndInsert}
          >
            📋 Insert Findings into Narrative Editor
          </button>
        </div>
      </div>
    </div>
  );
}
